import { Link } from "react-router-dom";
import FanchaLayout from "@/components/feature/FanchaLayout";
import useSeo from "@/hooks/useSeo";
import { SITE_NAME } from "@/mocks/fancha";

const PRINCIPLES = [
  { icon: "ri-git-merge-line", title: "以证据为锚", text: "每条内容都建立在公开、可核实的线索上，拒绝只凭一张截图就下结论的「开局一张图」。" },
  { icon: "ri-time-line", title: "还原时间线", text: "先讲完整来龙去脉，再谈评价。不挑对自己叙事有利的片段，不刻意省略关键转折。" },
  { icon: "ri-user-unfollow-line", title: "对人设不对人", text: "我们拆解的是「人设与事实的落差」，不人肉、不扣帽、不围观猎奇当事人隐私。" },
  { icon: "ri-emotion-line", title: "给结论留空间", text: "重大反转先让子弹飞，尊重真相需要时间发酵的规律，也尊重每一位观众独立思考的权利。" },
];

const JOURNEY = [
  { time: "2023", title: "从一场「大型翻车」说起", text: "我们观察到当红人设接连崩塌时，全网都在看热闹，却很少有人讲清「为什么会塌」。反差吃瓜局由此立项。" },
  { time: "2024", title: "确立内容方法论", text: "围绕人设反转、身份揭秘、滤镜打假、言行双标四条线索，形成一套「讲证据、还原时间线」的深扒体系。" },
  { time: "2025", title: "沉淀深度档案", text: "把单条热点沉淀为可反复回看的「反转档案」，让每一次围观都留有系统的记录与复盘。" },
  { time: "2026", title: "回归理性吃瓜", text: "在流量与喧嚣中，我们更坚定做那个「让子弹飞一会」的角色——理性，是我们给读者最持久的承诺。" },
];

const VOICES = [
  { name: "一只深夜刷手机的人", tag: "关注反差档案", text: "看多了塌房，其实是在提醒自己：任何滤镜下的人设，都值得多留一个心眼。谢谢这里讲逻辑不讲情绪。" },
  { name: "传媒系在读", tag: "内容原则认同者", text: "能把吃瓜做成「方法论」很厉害，尤其是「还原时间线」和「不人肉」这两条，真的在倡导理性围观。" },
  { name: "关注吃瓜三年的读者", tag: "深度专题爱好者", text: "反转档案的时间线整理得很清楚，前后对比一目了然，看得出是认真核实过再做内容的。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于反差吃瓜局 - 人设反转深扒与理性吃瓜的内容媒体",
    description:
      "了解反差吃瓜局：专注人设反转、身份揭秘、前后对比与言行双标的内容媒体。我们坚持以证据为锚、还原完整时间线、反对网络暴力，倡导理性吃瓜与独立思考。",
    keywords: "反差吃瓜,反差吃瓜局,反差吃瓜人设反转,反差吃瓜当事人,理性吃瓜",
    canonicalPath: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        description: "专注人设反转、身份揭秘、前后对比与言行双标的内容媒体，倡导理性吃瓜与独立思考。",
        email: "contact@fancha.example",
      },
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: `关于${SITE_NAME}`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "关于我们" },
        ],
      },
    ],
  });

  return (
    <FanchaLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-foreground-950 text-background-100">
        <span className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-accent-500/20 blur-3xl" />
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-sm text-background-200/70">
            <Link to="/" className="whitespace-nowrap hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="whitespace-nowrap text-background-100">关于我们</span>
          </nav>
          <div className="mt-8 max-w-3xl">
            <p className="inline-flex items-center gap-2 rounded-full bg-background-100/10 px-3 py-1 text-xs font-semibold text-accent-300">
              <i className="ri-user-unfollow-line" /> 反差吃瓜局 · 关于我们
            </p>
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight md:text-5xl">
              把「台前的人设」
              <br />
              与「台后的真相」并排摊开
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-8 text-background-200/85">
              {SITE_NAME} 是一档专注「人设反转、身份揭秘、前后对比、言行双标」的内容媒体。我们不追热点的皮毛，只做一件事：冷静、客观、讲证据地把一个人设的「光鲜面」与「另一面」摆在一起。
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-2">
            {["人设反转", "身份揭秘", "滤镜打假", "言行双标", "打脸实录"].map((t) => (
              <span key={t} className="whitespace-nowrap rounded-full border border-background-100/20 px-3 py-1 text-xs text-background-200/70">
                #{t}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        {/* 定位 */}
        <section className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">POSITION</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">我们的网站定位</h2>
            <p className="mt-4 leading-8 text-foreground-700">
              在信息爆炸的「吃瓜时代」，比热闹更稀缺的是「把话讲清楚」。我们把散落在各处的碎片信息，整理成有时间线、有逻辑、有证据的反转档案，让每一位读者都能快速看清事件全貌。
            </p>
            <p className="mt-3 leading-8 text-foreground-700">
              我们不替任何人站队，也不刻意迎合情绪。我们相信，真正有价值的围观，是带着独立思考去看清来龙去脉，而非跟风起哄。
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { num: "人设反转", label: "拆解表里不一的叙事" },
              { num: "身份揭秘", label: "验证标签与身份真相" },
              { num: "前后对比", label: "把反差摆到明面" },
              { num: "理性吃瓜", label: "反对网暴与断章取义" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-background-200/70 bg-background-100/50 p-6">
                <i className="ri-verified-badge-line text-2xl text-primary-500" />
                <p className="mt-3 font-heading text-lg font-bold text-foreground-950">{s.num}</p>
                <p className="mt-1 text-sm text-foreground-600">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 内容原则 */}
        <section id="principle" className="mt-16 scroll-mt-20 rounded-3xl bg-background-100/60 p-6 md:p-10">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">PRINCIPLE</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">内容原则</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-foreground-600">
              四条铁律，贯穿反差吃瓜局的每一次深扒与每一篇文章。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {PRINCIPLES.map((p) => (
              <div key={p.title} className="flex gap-4 rounded-2xl border border-background-200/70 bg-background-50 p-6">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                  <i className={`${p.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 发展理念 */}
        <section id="journey" className="mt-16 scroll-mt-20">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">PHILOSOPHY</p>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">发展理念</h2>
              <p className="mt-2 max-w-xl text-sm text-foreground-600">
                从围观热点到沉淀内容，我们一步步把「吃瓜」做成一件有方法论、有责任感的长期事业。
              </p>
            </div>
          </div>
          <ol className="relative mt-8 ml-2 space-y-8 border-l-2 border-background-200 pl-8">
            {JOURNEY.map((j) => (
              <li key={j.time} className="relative">
                <span className="absolute -left-[41px] top-1 flex h-5 w-5 items-center justify-center">
                  <span className="h-2.5 w-2.5 rounded-full bg-accent-500 ring-4 ring-background-50" />
                </span>
                <p className="inline-flex items-center rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">{j.time}</p>
                <h3 className="mt-2 font-heading text-lg font-bold text-foreground-950">{j.title}</h3>
                <p className="mt-1.5 max-w-3xl text-sm leading-7 text-foreground-600">{j.text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* 用户口碑 */}
        <section className="mt-16">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">VOICES</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">读者怎么说</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {VOICES.map((v) => (
              <figure key={v.name} className="flex flex-col rounded-2xl border border-background-200/70 bg-background-50 p-6">
                <div className="flex gap-1 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill" />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-7 text-foreground-700">
                  “{v.text}”
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200/70 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-user-heart-line" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-900">{v.name}</span>
                    <span className="block text-xs text-foreground-500">{v.tag}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* 联系方式 */}
        <section id="contact" className="mt-16 scroll-mt-20 overflow-hidden rounded-3xl bg-foreground-950 p-6 text-background-100 md:p-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="inline-flex w-max items-center gap-2 rounded-full bg-accent-400/15 px-3 py-1 text-xs font-semibold text-accent-300">
                <i className="ri-mail-send-line" /> CONTACT
              </p>
              <h2 className="mt-3 font-heading text-2xl font-black md:text-3xl">联系我们</h2>
              <p className="mt-3 max-w-md text-sm leading-7 text-background-200/80">
                有合作意向、转载授权，或想投递一条可靠的吃瓜线索？欢迎通过邮箱或投稿表单与我们联系，我们会尽快回复。
              </p>
              <div className="mt-6 space-y-3 text-sm">
                <a
                  href="mailto:contact@fancha.example"
                  rel="nofollow"
                  className="inline-flex items-center gap-3 rounded-xl border border-background-100/15 px-4 py-3 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-200"
                >
                  <i className="ri-mail-line text-accent-300" />
                  contact@fancha.example
                </a>
                <div className="inline-flex items-center gap-3 rounded-xl border border-background-100/15 px-4 py-3 text-background-100">
                  <i className="ri-flashlight-line text-accent-300" />
                  投稿 / 线索 → 前往首页「提供线索」表单
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3 rounded-2xl bg-background-100/5 p-6">
              <h3 className="font-heading text-lg font-bold text-background-50">常见问题速览</h3>
              <p className="text-sm leading-6 text-background-200/75">
                想了解反差吃瓜局的事件真实性、投稿规则与内容立场？我们在首页的「常见问题」区做了统一说明。
              </p>
              <Link
                to="/#faq"
                className="mt-2 inline-flex w-max whitespace-nowrap items-center gap-2 rounded-full bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-background-100"
              >
                查看常见问题 <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/#contribute"
                className="mt-1 inline-flex w-max whitespace-nowrap items-center gap-2 rounded-full bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                去提交线索 <i className="ri-send-plane-fill" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </FanchaLayout>
  );
}